import { nextTick } from 'vue'
import type { Router } from 'vue-router'
import { Notify, Platform } from 'quasar'
import { usePushNotificationStore, useMessagingStore } from 'src/stores'
import { useOrgStore } from 'src/stores/org.store'
import { router as globalRouter } from 'src/router'
import type { INotificationData } from 'src/interfaces/notification'
import { useAuthStore } from 'src/common/store'
import { Roles } from 'src/common/types'

function isRouteAccessibleOnCurrentPlatform(
  routeName: string,
  router?: Router,
): boolean {
  const routerInstance = router || globalRouter
  if (!routerInstance) return false

  const route = routerInstance.getRoutes().find((r) => r.name === routeName)
  if (!route) return false

  const supportedPlatforms = route.meta?.supportedPlatforms as
    | plateform[]
    | undefined
  if (!supportedPlatforms || supportedPlatforms.length === 0) {
    return true
  }

  if (
    supportedPlatforms.includes('desktop') &&
    !supportedPlatforms.includes('mobile') &&
    !supportedPlatforms.includes('native')
  ) {
    return Platform.is.desktop
  }

  return true
}

export async function handlePushNotificationAction(
  data: INotificationData | undefined,
): Promise<void> {
  return handleNotificationAction(data, undefined, globalRouter)
}

export async function handleNotificationAction(
  data: INotificationData | undefined,
  router?: Router,
  globalRouter?: Router,
): Promise<void> {
  if (!data) {
    const store = usePushNotificationStore()
    store.setError('Notification data is undefined or null')
    return
  }

  await nextTick()

  await handleAdminCenterRedirection(data)

  if (data.deepLink) {
    navigateToDeepLink(data.deepLink, router, globalRouter)
  } else if (data.route) {
    navigateToRoute(data.route, data.params, data.queries, router, globalRouter)
  } else if (data.action) {
    handleCustomAction(data.action, router, globalRouter, data)
  }
}

export async function handleAdminCenterRedirection(
  data: INotificationData,
): Promise<void> {
  const authStore = useAuthStore()

  if (!authStore.user) {
    return
  }

  const userRole = authStore.user.role
  const isAdmin = [
    Roles.SuperAdmin,
    Roles.OrgAdmin,
    Roles.CenterAdmin,
  ].includes(userRole)

  if (!isAdmin) {
    return
  }

  if (!data.orgId || !data.centerId) {
    return
  }

  if (
    data.centerId !== authStore.currentCenterId ||
    data.orgId !== authStore.currentOrgId
  ) {
    authStore.setCurrentCenterId(data.centerId)
    authStore.setCurrentOrgId(data.orgId)
    window.location.reload()
  }
}

export function navigateToDeepLink(
  deepLink: string,
  router?: Router,
  globalRouter?: Router,
): void {
  try {
    const routerInstance = router || globalRouter

    if (!routerInstance) {
      const store = usePushNotificationStore()
      store.setError(
        `No router available for navigation. DeepLink: ${deepLink}`,
      )
      return
    }

    const url = new URL(deepLink, process.env.DOMAIN_NAME)
    const path = url.pathname
    const query = Object.fromEntries(url.searchParams)

    const matchedRoute = routerInstance
      .getRoutes()
      .find((r) => r.path === path || r.path.includes(path))

    if (matchedRoute && matchedRoute.name) {
      if (
        !isRouteAccessibleOnCurrentPlatform(
          matchedRoute.name as string,
          routerInstance,
        )
      ) {
        return
      }

      const sanitizedQueries = sanitizeTabQueries(
        matchedRoute.name as string,
        query,
      )
      if (sanitizedQueries === null) {
        return
      }

      void routerInstance.push({ path, query: sanitizedQueries })
    } else {
      void routerInstance.push({ path, query })
    }
  } catch (e) {
    const error = e as { message: string }
    const store = usePushNotificationStore()
    store.setError(
      `Deep link error: ${error.message}. DeepLink: ${deepLink}, Domain: ${
        process.env.DOMAIN_NAME || 'fallback'
      }`,
    )
  }
}

export function navigateToRoute(
  route: string,
  params: Record<string, any> | undefined,
  queries: Record<string, any> | undefined,
  router?: Router,
  globalRouter?: Router,
): void {
  try {
    const routerInstance = router || globalRouter

    if (!routerInstance) {
      const store = usePushNotificationStore()
      store.setError(`No router available for navigation. Route: ${route}`)
      return
    }

    if (!isRouteAccessibleOnCurrentPlatform(route, routerInstance)) {
      return
    }
    const sanitizedQueries = sanitizeTabQueries(route, queries)
    if (queries && sanitizedQueries === null) {
      return
    }

    void routerInstance.push({
      name: route,
      params: params || {},
      query: sanitizedQueries || {},
    })
  } catch (e) {
    const error = e as { message: string }
    const store = usePushNotificationStore()
    store.setError(
      `Route navigation error: ${error.message}. Route: ${route}, Params: ${JSON.stringify(
        params,
      )}, Queries: ${JSON.stringify(queries)}`,
    )
  }
}

function sanitizeTabQueries(
  routeName: string,
  queries?: Record<string, any>,
): Record<string, any> | null {
  if (!queries) return queries || {}

  if (Platform.is.desktop) return queries

  const desktopOnlyTabsByRoute: Record<string, string[]> = {
    'admin.dashboard': ['Enrollment', 'Attendance', 'Expiries', 'Info Changes'],
  }

  if (desktopOnlyTabsByRoute[routeName]) {
    const desktopOnlyTabs = desktopOnlyTabsByRoute[routeName]
    const tab = queries.tab || queries.subTab

    if (tab && desktopOnlyTabs.includes(tab as string)) {
      return null
    }
  }

  return queries
}

export function handleCustomAction(
  action: string,
  router?: Router,
  globalRouter?: Router,
  data?: INotificationData,
): void {
  try {
    const routerInstance = router || globalRouter

    if (!routerInstance) {
      const store = usePushNotificationStore()
      store.setError(`No router available for custom action: ${action}`)
      return
    }

    const authStore = useAuthStore()

    // Handle custom notification actions
    switch (action) {
      case 'open_dashboard':
        void routerInstance.push({ name: 'admin.dashboard' })
        break
      case 'open_message': {
        if (authStore.isClient) {
          void routerInstance.push({ name: 'client.messenger' })
        } else {
          const messagingStore = useMessagingStore()
          const familyId = data?.additionalData?.familyId as string | undefined
          const memberId = data?.additionalData?.memberId as string | undefined
          const conversationId = data?.additionalData?.conversationId as
            | string
            | undefined

          if (familyId && memberId) {
            messagingStore.openToMember(familyId, memberId)
          } else if (conversationId) {
            messagingStore.openToConversation(conversationId)
          } else {
            messagingStore.openMessager()
          }
        }
        break
      }
      case 'open_announcement': {
        if (authStore.isClient) {
          void routerInstance.push({ name: 'client.messenger' })
        } else {
          const messagingStore = useMessagingStore()
          messagingStore.openToAnnouncements()
        }
        break
      }
      // Add more actions as needed
      default: {
        const store = usePushNotificationStore()
        store.setError(`Unknown custom action: ${action}`)
      }
    }
  } catch (error) {
    const errorCast = error as { message: string }
    const store = usePushNotificationStore()
    store.setError(
      `Custom action error: ${errorCast.message}. Action: ${action}`,
    )
  }
}

async function switchCenter(centerId: string): Promise<void> {
  const authStore = useAuthStore()
  const orgStore = useOrgStore()

  const center = orgStore.centers.find((c) => c.id === centerId)

  if (!center) {
    Notify.create({
      type: 'negative',
      message: 'Center not found',
      position: 'top',
    })
    return
  }

  authStore.setCurrentCenterId(centerId)
  window.location.reload()
}

export function showCrossCenterNotification(
  centerId: string,
  centerName: string,
): void {
  Notify.create({
    message: 'New notification',
    caption: `${centerName}`,
    position: 'top',
    progress: true,
    timeout: 7000,
    multiLine: false,
    actions: [
      {
        label: 'Switch',
        color: 'white',
        handler: () => {
          void switchCenter(centerId)
        },
      },
      {
        icon: 'close',
        color: 'grey-5',
      },
    ],
    icon: 'notifications',
    classes: '!tw-bg-[#293f45]',
    textColor: 'white',
  })
}
